import { SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { Product, ProductStock } from '@setvi/shared/interfaces';
import { getStock } from 'pages/products/components';
import { Customer } from 'Services/Mongo/Types';
import { Box, Tooltip, withStyles } from '@material-ui/core';
import { Skeleton as MUISkeleton } from '@material-ui/lab';
import { useRealTimeInventory } from 'pages/products/components/stock/useRealtimeInventory';
import { useSingerLabel } from 'hooksv2';
import { isWarehouseStocked } from '../../../../subpages/warehouse/columns-content/components/stocked';
import { ProductValueWrapper } from '../product-value';

const Skeleton = withStyles({
  root: {
    backgroundColor: 'rgba(23, 23, 37, 0.11) !important'
  }
})(MUISkeleton);

interface ProductHeaderStockProps {
  product: Product;
  selectedCustomer?: Customer;
  stockRefresh?: ProductStock['refresh'];
  updateProduct: (product: Product, props: Partial<Product>) => void;
  onClick?: () => void;
}

export const ProductHeaderStock = ({
  product,
  selectedCustomer,
  stockRefresh,
  updateProduct,
  onClick
}: ProductHeaderStockProps) => {
  const { fetchRealtimeInventory, loading: isLoading } = useRealTimeInventory();
  const { isSinger } = useSingerLabel();
  const inStockLabel = isSinger ? 'Available' : 'In stock';

  const stock = getStock(product.inventory, selectedCustomer);
  const dropship = product?.dropship || false;

  const handleFetchRealtimeInventory = async (id: string) => {
    const res = await fetchRealtimeInventory(id);
    Array.isArray(res) && updateProduct?.(product, { inventory: res });
  };

  const content = () => {
    if (isLoading) return <Skeleton width={100} />;
    if (dropship)
      return (
        <SText title="Dropship" weight="semibold" fontColor={TextColor.Blue} />
      );
    if (!stock)
      return (
        <SText
          title={isSinger ? 'Not Available' : 'Out of stock'}
          weight="semibold"
          fontColor={TextColor.Red}
        />
      );
    return (
      <Tooltip
        arrow
        placement="top"
        title={
          <Box p={1}>
            <Box pb={1}>
              <b>Available in Warehouse</b>
            </Box>
            <ul
              style={{
                padding: 0,
                margin: 0,
                listStyle: 'none'
              }}>
              {product?.inventory?.length === 0 && (
                <li>No stock information available. </li>
              )}
              {product?.inventory?.map(row => (
                <li key={row.warehouse?._id}>
                  {isSinger &&
                    product?.stockInfo &&
                    `${isWarehouseStocked(row, product.stockInfo) ? 'S' : 'N'} - `}
                  {row.warehouse?.name} - {inStockLabel}: {row.availableOnHand}
                </li>
              ))}
            </ul>
          </Box>
        }>
        <ProductValueWrapper>
          <SText
            lineHeight="24px"
            title={`${inStockLabel}:`}
            weight="bold"
            fontColor={TextColor.SlateGrey}
            style={{ marginRight: 4 }}
          />
          <SText
            lineHeight="24px"
            title={stock.toString()}
            fontColor={TextColor.Green}
            style={{ textDecoration: 'underline' }}
          />
        </ProductValueWrapper>
      </Tooltip>
    );
  };

  return (
    <Box
      style={{ cursor: stockRefresh ? 'pointer' : 'default' }}
      onClick={() => {
        onClick?.();
        stockRefresh && handleFetchRealtimeInventory(product.sku);
      }}>
      {content()}
    </Box>
  );
};
